import React, { useEffect, useState, useRef } from 'react';
import { ArrowRightLeft, Loader2, Search, X } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { getCompanyDisplayName, getContactFullName } from '../utils/companyDisplayName';

/**
 * Fase 3 (Contatos 3C) — TransferContactModal.
 *
 * Move contato de uma empresa para outra preservando histórico:
 * - Fecha contact_employments antigo (is_current=false, end_date)
 * - Cria novo employment ativo
 * - Atualiza contatos.empresa_id legado
 * - Opcional: novo cargo, novo email
 *
 * Backend: rpc_transfer_contact_to_company.
 */
const TransferContactModal = ({ contact, currentEmpresa, onClose, onTransferred }) => {
  const [empresaQuery, setEmpresaQuery] = useState('');
  const [empresaResults, setEmpresaResults] = useState([]);
  const [empresaSelected, setEmpresaSelected] = useState(null);
  const [searchingEmpresa, setSearchingEmpresa] = useState(false);
  const [newTitle, setNewTitle] = useState(contact?.title || '');
  const [newEmail, setNewEmail] = useState(contact?.email || '');
  const [exitDate, setExitDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const searchTimerRef = useRef(null);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape' && !saving) onClose?.(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose, saving]);

  useEffect(() => {
    if (empresaSelected) return undefined;
    if (!empresaQuery || empresaQuery.trim().length < 2) {
      setEmpresaResults([]);
      return undefined;
    }
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(async () => {
      setSearchingEmpresa(true);
      const q = empresaQuery.trim();
      const { data } = await supabase
        .from('empresas')
        .select('id, company_name, short_name, empresa, papel_negocio')
        .or(`company_name.ilike.%${q}%,short_name.ilike.%${q}%,empresa.ilike.%${q}%`)
        .order('company_name')
        .limit(10);
      const filtered = (data || []).filter((e) => e.id !== currentEmpresa?.id);
      setEmpresaResults(filtered);
      setSearchingEmpresa(false);
    }, 300);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [empresaQuery, empresaSelected, currentEmpresa?.id]);

  if (!contact) return null;

  const fullName = getContactFullName(contact) || '(sem nome)';
  const fromDisplay = getCompanyDisplayName(currentEmpresa);
  const toDisplay = empresaSelected ? getCompanyDisplayName(empresaSelected) : null;

  const handleSubmit = async () => {
    if (!empresaSelected?.id) { setError('Selecione a empresa destino'); return; }
    setError(null);
    setSaving(true);
    try {
      const { data, error: err } = await supabase.rpc('rpc_transfer_contact_to_company', {
        p_contact_id: contact.id,
        p_new_empresa_id: empresaSelected.id,
        p_old_empresa_id: currentEmpresa?.id || null,
        p_new_title: newTitle?.trim() || null,
        p_new_email: newEmail?.trim() || null,
        p_exit_date: exitDate,
        p_entry_date: exitDate,
      });
      if (err) throw err;
      window.alert(`${fromDisplay} | ${fullName} → ${toDisplay} | ${fullName}`);
      onTransferred?.(data);
      onClose?.();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Transferir contato"
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose?.(); }}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <ArrowRightLeft size={16} className="text-orange-600" />
            <h3 className="text-sm font-semibold text-slate-800">Transferir contato</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Fechar"
            className="p-1 rounded hover:bg-slate-200 text-slate-500 disabled:opacity-50"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-4 space-y-3 text-sm">
          <div className="bg-slate-50 border border-slate-200 rounded p-2 text-xs">
            <div className="font-semibold text-slate-800">{fullName}</div>
            {fromDisplay && <div className="text-slate-500 mt-0.5">{fromDisplay} {contact.title ? `· ${contact.title}` : ''}</div>}
          </div>

          <div className="block">
            <span className="block text-[11px] font-semibold text-slate-600 mb-1">Empresa destino *</span>
            {empresaSelected ? (
              <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5">
                <div className="text-sm font-medium text-emerald-900 truncate">→ {toDisplay}</div>
                <button
                  type="button"
                  onClick={() => { setEmpresaSelected(null); setEmpresaQuery(''); }}
                  className="text-emerald-700 hover:text-emerald-900 text-xs flex items-center gap-1"
                >
                  <X size={12} /> trocar
                </button>
              </div>
            ) : (
              <div className="relative">
                <div className="flex items-center gap-1.5 border border-slate-300 rounded px-2 py-1.5">
                  <Search size={12} className="text-slate-400" />
                  <input
                    type="text"
                    value={empresaQuery}
                    onChange={(e) => setEmpresaQuery(e.target.value)}
                    placeholder="Digite ≥2 letras (CONX, LAVVI, BTG...)"
                    className="flex-1 outline-none text-sm"
                    autoComplete="off"
                  />
                  {searchingEmpresa && <Loader2 size={12} className="animate-spin text-slate-400" />}
                </div>
                {empresaResults.length > 0 && (
                  <ul className="absolute left-0 right-0 top-full mt-1 z-20 bg-white border border-slate-200 rounded shadow max-h-56 overflow-y-auto">
                    {empresaResults.map((e) => (
                      <li key={e.id}>
                        <button
                          type="button"
                          onClick={() => { setEmpresaSelected(e); setEmpresaResults([]); setEmpresaQuery(''); }}
                          className="w-full text-left px-2 py-1.5 hover:bg-slate-50 text-sm"
                        >
                          <div className="font-medium text-slate-800">{getCompanyDisplayName(e)}</div>
                          {e.company_name !== getCompanyDisplayName(e) && (
                            <div className="text-[10px] text-slate-500 truncate">{e.company_name}</div>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          <label className="block">
            <span className="block text-[11px] font-semibold text-slate-600 mb-1">Novo cargo</span>
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="ex: Coordenador de Novos Negócios"
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
            />
          </label>

          <label className="block">
            <span className="block text-[11px] font-semibold text-slate-600 mb-1">Novo email</span>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="email no novo domínio"
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
            />
          </label>

          <label className="block">
            <span className="block text-[11px] font-semibold text-slate-600 mb-1">Data de saída / entrada</span>
            <input
              type="date"
              value={exitDate}
              onChange={(e) => setExitDate(e.target.value)}
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
            />
            <span className="block text-[10px] text-slate-400 mt-0.5">
              Mesma data fecha vínculo na empresa antiga e abre na nova
            </span>
          </label>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-2 text-[12px] text-red-800">
              {error}
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-3 py-1.5 text-[12px] bg-white border border-slate-300 text-slate-700 rounded hover:bg-slate-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || !empresaSelected}
            className="px-3 py-1.5 text-[12px] bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50 flex items-center gap-1"
          >
            {saving && <Loader2 size={12} className="animate-spin" />}
            {saving ? 'Transferindo…' : 'Transferir'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransferContactModal;
